import React from 'react';
import { Wind, Droplets, Flame, Snowflake, Zap, CircleDot } from 'lucide-react';

const items = [
  {
    slug: 'pneumatik',
    icon: Wind,
    title: 'Pneumatik',
    alt:
      'Industrieller pneumatischer Ventilblock mit polierten Edelstahl-Druckluftventilen, Messing-Verschraubungen und blau-grauen, farblich codierten Druckluftschläuchen in einer Werkshalle — typische Anwendung für Druckluft-Leckage­ortung',
    caption:
      'Pneumatik-Anwendung der MIBAX Ultraschall-Leckage­ortung — Druckluft­netze in Fertigungs­linien, Montage­robotern und Automatisierungs­zellen.',
    body:
      'Speziell im Bereich der Pneumatik finden sich oft mehrere Leckagen auf engstem Raum. Unsere modernste High-End-Technologie macht alle Leckstellen gleichzeitig sichtbar — kleine Lecks werden auch im Beisein großer nicht mehr übersehen. Das bedeutet einzigartige Zuverlässigkeit und massive Zeitersparnis bei jedem Audit.',
  },
  {
    slug: 'technische_gase',
    icon: Droplets,
    title: 'Technische Gase',
    alt:
      'Industrielle Gas­versorgungsstation mit Edelstahl-Druckminderern, Manometern und Hochdruck-Gasflaschen für Stickstoff, Argon und Helium, angekettet an einer Betonwand im Außenbereich',
    caption:
      'Technische Gase wie Stickstoff (N₂), Argon (Ar), Kohlendioxid (CO₂), Helium (He) und Wasserstoff (H₂) in Versorgungs­netzen und Produktions­anlagen.',
    body:
      'Neben Druckluft prüfen wir verschiedenste technische Gase — Stickstoff, Argon, Kohlendioxid, Helium oder Wasserstoff. Selbst aus großen Entfernungen lassen sich Leckagen punktgenau lokalisieren. Möglich macht das eine extrem hohe Sensitivität in Verbindung mit optischer Zoom-Erfassung.',
  },
  {
    slug: 'brennbare_gase',
    icon: Flame,
    title: 'Brennbare Gase',
    alt:
      'Gelb lackierte Erdgas-Pipeline mit Warnmarkierungen, Richtungs­pfeilen und Druckangabe, montiert auf Beton­stützen unter freiem Himmel — Industrie­anlage für Erdgas­transport',
    caption:
      'Brennbare Gase wie Erdgas, Methan, Propan und Biogas in Industrie­leitungen, Heizkraftwerken und Biogas­anlagen.',
    body:
      'Unser Messverfahren arbeitet bereits ab kleinsten Systemdrücken um 250 mbar und findet Leckagen aus deutlich größerer Entfernung als klassische Gas-Schnüffler. Abgedeckt werden Erdgas, Methan, Propan und Biogas — sicher, schnell und ohne direkten Kontakt zur Leitung.',
  },
  {
    slug: 'kaeltemittel',
    icon: Snowflake,
    title: 'Kältemittelanlagen',
    alt:
      'Industrielle Ammoniak- und CO₂-Kälteanlage mit vertikalen Edelstahltanks, isolierter Verrohrung, Ventilen und teils mit Frost überzogenen Leitungen unter blauem Himmel',
    caption:
      'Industrielle Kälteanlagen mit Ammoniak (NH₃) oder CO₂ als Kältemittel — Lebensmittel­industrie, Logistik­kühlung und Prozess­kühlung.',
    body:
      'Ammoniak- und CO₂-Kälteanlagen tolerieren nur geringste Leckraten. Durch die einzigartige Sensitivität unserer Technologie und ihre große Reichweite werden auch industrielle Großanlagen mühelos und vollständig geprüft — ohne Produktionsunterbrechung.',
  },
  {
    slug: 'teilentladung',
    icon: Zap,
    title: 'Teilentladungen / Coronaeffekt',
    alt:
      'Hochspannungs-Freileitung mit mehreren Porzellan-Isolatoren und Strom­leitern auf einem stählernen Gittermast vor klarem blauem Himmel — Anwendung für Detektion von Teilentladungen und Corona-Effekten',
    caption:
      'Teilentladungs- und Corona-Detektion an Hochspannungs­leitungen, Umspannwerken, Transformatoren und Schalt­anlagen.',
    body:
      'Selbst in lauter Umgebung und aus bis zu 120 m Entfernung detektieren wir Teilentladungen an Hochspannungs­anlagen. Großflächiges Scannen und kontaktlose Messung bringen einen klaren Zeitvorteil gegenüber konventionellen Verfahren — bei voller Anlagensicherheit.',
  },
  {
    slug: 'vakuum',
    icon: CircleDot,
    title: 'Vakuum',
    alt:
      'Industrielles Edelstahl-Vakuumsystem mit ISO-K-Flansch­verrohrung, großem Absperr­schieber, Vakuum­messgerät und Turbomolekular­pumpe in einer hellen Laborumgebung',
    caption:
      'Vakuum­systeme in Halbleiter-, Beschichtungs- und Forschungs­anwendungen — Detektion unsichtbarer Lecks, die in das System hinein­gesaugt werden.',
    body:
      'Anders als bei Druckluftlecks geht der Schall bei Vakuumlecks in das System hinein — und entgeht damit konventionellen Verfahren. Unsere High-End-Messtechnik erkennt auch diese Lecks zuverlässig dank beispielloser Empfindlichkeit.',
  },
];

export default function Applications() {
  return (
    <section
      id="anwendungen"
      className="py-16 sm:py-24 md:py-32 relative border-y border-slate-200/70 bg-white"
      data-testid="applications-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Einsatzbereiche</div>
            <h2 className="font-display font-black text-slate-900 text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.02]">
              Sechs Disziplinen.<br />
              <span className="text-[#0891b2]">Eine Technologie.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
              Über die klassische Druckluft hinaus prüfen wir mit modernster
              High-End-Messtechnik <strong>technische Gase</strong>,
              <strong> brennbare Gase</strong>,
              <strong> Kältemittel­anlagen</strong>,
              <strong> Vakuumsysteme</strong> sowie
              <strong> Teilentladungen / Corona-Effekte</strong> an
              Hochspannungs­anlagen. Vom Sensor­array über die Signal­auswertung
              bis zum Auswertungs­report — alles aus einer Hand,
              ISO-50001-konform dokumentiert.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {items.map((it, idx) => {
            const Icon = it.icon;
            const reversed = idx % 2 === 1; // alternate row direction for rhythm
            return (
              <article
                key={it.slug}
                className={`grid md:grid-cols-12 gap-0 card-surface overflow-hidden ${
                  reversed ? 'md:[&>*:first-child]:order-2' : ''
                }`}
                data-testid={`application-${it.slug}`}
              >
                <figure className="md:col-span-5 relative bg-slate-100 m-0">
                  <img
                    src={`/applications/${it.slug}.jpg`}
                    alt={it.alt}
                    width="1200"
                    height="655"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover aspect-[16/9] md:aspect-auto md:min-h-[260px]"
                  />
                  <figcaption className="sr-only">{it.caption}</figcaption>
                </figure>

                <div className="md:col-span-7 p-6 sm:p-7 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 border border-[#0891b2]/30 bg-[#0891b2]/5 grid place-items-center">
                      <Icon className="w-4 h-4 text-[#0891b2]" strokeWidth={2} />
                    </div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Anwendung · {String(idx + 1).padStart(2, '0')} / 06
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-slate-900 text-xl sm:text-2xl md:text-3xl tracking-tight mb-3 leading-tight">
                    {it.title}
                  </h3>
                  <p className="text-slate-600 text-[15px] leading-relaxed max-w-2xl">
                    {it.body}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
